import { useEffect, useState } from "react"

import { ErroDaApi, api, type ConferenciaDeAutenticidade } from "../api"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import * as Icones from "../componentes/Icones"
import { Selo, chaveConhecida } from "../componentes/Selo"
import { dataHora, nivelDeAssinatura, numeroComAno } from "../formato"
import { VAZIO } from "../lib/classes"
import { cn } from "../lib/cn"

const MARCA_DA_DEMONSTRACAO = "DEMONSTRAÇÃO · dados fictícios"  // anti-ia-ok: LEI 2, e a única página pública

const ROTULO = "text-(length:--texto-xs) font-medium tracking-(--tracking-rotulo) text-(--sobre-azul-100-fraca) uppercase [[data-tema=escuro]_&]:text-(--tinta-3)"
const BORDA_QUE_NAO_CONFERE = "border-status-atencao" // anti-ia-ok: token de cor de status, não texto de tela
const TINTA_DA_DEMONSTRACAO = "text-status-atencao" // anti-ia-ok: token de cor de status, não texto de tela

export function Autenticidade({ codigo }: { codigo: string }) {
  const [conferencia, definirConferencia] = useState<ConferenciaDeAutenticidade | null>(null)
  const [erro, definirErro] = useState<string | null>(null)
  const [buscando, definirBuscando] = useState(true)

  useEffect(() => {
    let vivo = true
    definirBuscando(true)
    api
      .autenticidade(codigo)
      .then((corpo) => {
        if (!vivo) return
        definirConferencia(corpo)
        definirErro(null)
      })
      .catch((falha: unknown) => {
        if (!vivo) return
        definirConferencia(null)
        definirErro(
          falha instanceof ErroDaApi && falha.codigo === 404
            ? "Não há laudo com este código neste sistema. Confira os dezesseis caracteres impressos no rodapé do documento."
            : falha instanceof Error
              ? falha.message
              : "Não foi possível conferir o documento no momento.",
        )
      })
      .finally(() => {
        if (vivo) definirBuscando(false)
      })
    return () => {
      vivo = false
    }
  }, [codigo])

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-(--fundo) px-4 py-4">
      <div className="flex w-full max-w-2xl flex-col gap-4">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="text-(length:--texto-lg) font-[650] tracking-(--tracking-rotulo) text-(--sobre-azul-100) uppercase bg-azul-200 p-2 px-3 rounded-xl [[data-tema=escuro]_&]:border [[data-tema=escuro]_&]:border-(--borda) [[data-tema=escuro]_&]:bg-(--fundo) [[data-tema=escuro]_&]:text-(--tinta-2)">
            ATHUS Contratos
          </span>
          <h1 className="text-(length:--texto-xl)">Conferência de autenticidade</h1>
          <p className="mx-auto max-w-lg text-(length:--texto-sm) text-(--muted-foreground)">
            Esta página valida a autenticidade de laudos de fiscalização emitidos pelo sistema,
            garantindo a integridade do documento impresso. Ela mostra apenas o que já consta do
            documento impresso.
          </p>
        </header>

        <Card>
          <CardHeader className="border-b border-(--borda) pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>Código de verificação</CardTitle>
              <span className="rounded-(--raio-controle) border border-(--borda) bg-(--fundo) px-2 py-0.5 font-mono text-(length:--texto-lg) text-(--tinta-2)">
                {codigo}
              </span>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col gap-4 pt-4">
            {buscando ? (
              <p className={cn(VAZIO, "py-6 text-center")}>Conferindo o documento...</p>
            ) : erro !== null ? (
              <div
                className="flex items-start gap-3 rounded-(--raio-controle) border border-(--vermelho) bg-(--superficie) p-3"
                role="alert"
              >
                <span className="mt-0.5 text-(--vermelho)">
                  <Icones.Ressalva tamanho={18} />
                </span>
                <div className="flex flex-col gap-1">
                  <strong className="font-semibold">Não conferido</strong>
                  <p className="text-(length:--texto-sm) text-(--tinta)">{erro}</p>
                </div>
              </div>
            ) : conferencia === null ? null : (
              <>
                <div
                  data-slot="conferencia-veredicto"
                  data-confere={conferencia.selo_confere ? "sim" : "nao"}
                  className={cn(
                    "flex flex-col items-start gap-3 rounded-(--raio-controle) border bg-(--superficie) p-3 sm:flex-row sm:items-center",
                    conferencia.selo_confere ? "border-(--verde)" : BORDA_QUE_NAO_CONFERE,
                  )}
                >
                  <span className="shrink-0">
                    <Selo situacao={conferencia.selo_confere ? "selo_confere" : "selo_nao_confere"} />
                  </span>
                  <p className="text-(length:--texto-sm) leading-(--linha-densa) text-(--tinta)">
                    {conferencia.selo_confere
                      ? "O selo de segurança confere com os registros oficiais: o documento foi " +
                        "emitido e assinado digitalmente por este sistema. O código de conferência " +
                        "abaixo é o do arquivo assinado."
                      : "O selo deste laudo NÃO confere. O registro pode ter sido alterado ou o " +
                        "laudo ainda aguarda assinatura qualificada. A situação abaixo diz qual dos dois."}
                  </p>
                </div>

                {conferencia.is_demo && (
                  <p
                    data-slot="conferencia-demo"
                    className={cn(
                      "flex items-center gap-1.5 text-(length:--texto-xs) tracking-(--tracking-rotulo)",
                      TINTA_DA_DEMONSTRACAO,
                    )}
                  >
                    <Icones.Etiqueta />
                    {MARCA_DA_DEMONSTRACAO}
                  </p>
                )}

                <dl data-slot="conferencia-ficha" className="m-0 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Linha rotulo="Laudo">{conferencia.numero}</Linha>
                  <Linha rotulo="Tipo">{conferencia.rotulo_do_tipo}</Linha>
                  <Linha rotulo="Órgão">{conferencia.entidade}</Linha>
                  <Linha rotulo="Contrato">
                    {`${numeroComAno(conferencia.contrato_numero, conferencia.contrato_ano)}`}
                  </Linha>
                  <Linha rotulo="Assinado por">{conferencia.signatario_nome}</Linha>
                  <Linha rotulo="Assinado em">
                    {conferencia.assinado_em === null ? null : dataHora(conferencia.assinado_em)}
                  </Linha>
                  <Linha rotulo="Nível da assinatura">{nivelDeAssinatura(conferencia.nivel)}</Linha>
                  <Linha rotulo="Situação">
                    <Selo situacao={chaveConhecida(conferencia.situacao, "nao_configurado")} />
                  </Linha>
                  <Linha rotulo="Código de conferência do arquivo (SHA-256)" largo>
                    <span className="font-mono text-(length:--texto-xs) break-all">
                      {conferencia.resumo_sha256}
                    </span>
                  </Linha>
                </dl>

                <p className="border-t border-(--borda) pt-3 text-(length:--texto-xs) text-(--tinta-3)">
                  <strong>Dica de auditoria:</strong> para conferir por conta própria, o setor de TI ou o
                  auditor pode calcular o resumo SHA-256 do arquivo digital e compará-lo com o código
                  acima.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}

function Linha({
  rotulo,
  largo = false,
  children,
}: {
  rotulo: string
  largo?: boolean
  children: React.ReactNode
}) {
  const vazio = children === null || children === undefined || children === ""
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-(--raio-controle) border border-(--borda) bg-(--azul-100) px-3 py-2 [[data-tema=escuro]_&]:bg-(--fundo)",
        largo && "sm:col-span-2",
      )}
    >
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="m-0 font-medium text-(--sobre-azul-100) tabular-nums wrap-anywhere [[data-tema=escuro]_&]:text-(--tinta)">
        {vazio ? <span className="text-(--sobre-azul-100-fraca) [[data-tema=escuro]_&]:text-(--tinta-3)">—</span> : children}
      </dd>
    </div>
  )
}
